import { useState, useRef } from "react";
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Modal from "./Modal";
import SubmitButton from "../SubmitButton";

type DocumentFormat = "PDF" | "XML" | "HTML" | "TEXT";

type DocumentTypeOption = { txa_id: string; label: string };

type UploadDocumentModalProps = {
  documentTypeOptions: DocumentTypeOption[];
  onClose: () => void;
  onSubmit: (documentType: string, content: string, format: DocumentFormat) => void;
  submitting: boolean;
};

const ACCEPT = ".pdf,.xml,.html,.htm,.txt";

const mimeToFormat = (mime: string): DocumentFormat => {
  if (mime === "application/pdf") return "PDF";
  if (mime === "text/xml" || mime === "application/xml") return "XML";
  if (mime === "text/html") return "HTML";
  return "TEXT";
};

const extensionToFormat = (name: string): DocumentFormat => {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "PDF";
  if (ext === "xml") return "XML";
  if (ext === "html" || ext === "htm") return "HTML";
  return "TEXT";
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

type Tab = "file" | "text";

const UploadDocumentModal = ({ documentTypeOptions, onClose, onSubmit, submitting }: UploadDocumentModalProps) => {
  const [ activeTab, setActiveTab ] = useState<Tab>("file");
  const [ selectedFile, setSelectedFile ] = useState<File | null>(null);
  const [ textContent, setTextContent ] = useState("");
  const [ selectedType, setSelectedType ] = useState<DocumentTypeOption | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasContent = (activeTab === "file" && selectedFile !== null) ||
    (activeTab === "text" && textContent.trim().length > 0);
  const missingType = selectedType === null && hasContent;
  const canSubmit = !submitting && selectedType !== null && hasContent;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !selectedType) return;

    if (activeTab === "file" && selectedFile) {
      const base64 = await readFileAsBase64(selectedFile);
      const format = selectedFile.type
        ? mimeToFormat(selectedFile.type)
        : extensionToFormat(selectedFile.name);
      onSubmit(selectedType.txa_id, base64, format);
    } else if (activeTab === "text") {
      const base64 = btoa(textContent);
      onSubmit(selectedType.txa_id, base64, "PDF");
    }
  };

  const tabClass = (tab: Tab) =>
    `flex-1 py-2 text-sm font-medium text-center rounded-md transition-colors ${
      activeTab === tab
        ? "bg-white text-sky-700 shadow-sm"
        : "text-gray-500 hover:text-gray-700"
    }`;

  return (
    <Modal title="Upload Document" onClose={onClose}>
      <div className="mb-4">
        <label htmlFor="document-type" className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
        <select
          id="document-type"
          value={selectedType?.txa_id ?? ""}
          onChange={(e) => {
            const opt = documentTypeOptions.find((o) => o.txa_id === e.target.value) ?? null;
            setSelectedType(opt);
          }}
          className={`w-full rounded-md border px-3 py-2 text-sm bg-white focus:ring-1 ${
            missingType
              ? "border-amber-400 focus:border-amber-500 focus:ring-amber-500"
              : "border-gray-300 focus:border-sky-500 focus:ring-sky-500"
          }`}
        >
          <option value="">Select a document type</option>
          {documentTypeOptions.map((opt) => (
            <option key={opt.txa_id} value={opt.txa_id}>{opt.label}</option>
          ))}
        </select>
        {missingType && (
          <p className="mt-1 text-xs text-amber-600">Please select a document type before uploading.</p>
        )}
      </div>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-5">
        <button type="button" className={tabClass("file")} onClick={() => setActiveTab("file")}>
          Upload File
        </button>
        <button type="button" className={tabClass("text")} onClick={() => setActiveTab("text")}>
          Manually Enter Content
        </button>
      </div>

      {activeTab === "file" && (
        <div className="space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
          />
          {selectedFile ? (
            <div className="relative border-2 border-dashed border-sky-300 rounded-lg p-8 text-center bg-sky-50/50">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="absolute top-2 right-2 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
              <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
              <p className="text-xs text-gray-500 mt-1">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 text-xs text-sky-600 hover:text-sky-800 font-medium transition-colors"
              >
                Change file
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-sky-400 transition-colors"
            >
              <ArrowUpTrayIcon className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">Click to select a file</p>
              <p className="text-xs text-gray-400 mt-1">PDF, XML, HTML, or TXT</p>
            </button>
          )}
        </div>
      )}

      {activeTab === "text" && (
        <div className="space-y-3">
          <div>
            Format: <strong>PDF</strong>
          </div>
          <div>
            <label htmlFor="doc-content" className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              id="doc-content"
              rows={8}
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Enter document content..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 resize-y"
            />
          </div>
        </div>
      )}

      <div className="mt-6">
        <SubmitButton
          loading={submitting}
          text="Upload"
          loadingText="Uploading..."
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="bg-sky-600 hover:bg-sky-700 focus:ring-sky-500"
        />
      </div>
    </Modal>
  );
};

export default UploadDocumentModal;
